/**
 * MediaPipe FaceLandmarker tracker.
 *
 * Loads @mediapipe/tasks-vision at runtime via dynamic import from the JSDelivr
 * CDN. The package is intentionally NOT listed in package.json — it is a ~7 MB
 * WASM bundle that must not bloat the npm install graph.
 *
 * Landmark indices used for eyewear overlay:
 *   168 – nose bridge (mid)       primary anchor — glasses resting point (nose pad)
 *   6   – nose bridge (upper)     between eyebrows (used by jewelry/makeup adapters)
 *   33  – left eye outer corner   left frame edge
 *   263 – right eye outer corner  right frame edge
 *   468 – left iris centre        left lens centre  (iris refinement model)
 *   473 – right iris centre       right lens centre (iris refinement model)
 *   127 – left zygomatic          left temple attachment
 *   356 – right zygomatic         right temple attachment
 *   234 – left ear attachment     left arm end
 *   454 – right ear attachment    right arm end
 *   10  – forehead top            frame top boundary
 *   152 – chin                    face scale reference
 */

import type { ITracker, EyewearLandmarks } from './landmarks.js';
import { FaceShapeClassifier } from './face-shape-classifier.js';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MEDIAPIPE_CDN =
  'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.33/vision_bundle.mjs';

const WASM_BASE_URL =
  'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.33/wasm';

const MODEL_ASSET_PATH =
  'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';

/** Number of consecutive no-detection frames before we declare tracking lost. */
const STALE_THRESHOLD = 10;

/** Min inter-pupil distance (normalised) — face too far away. */
const MIN_IPD = 0.04;

/** Max inter-pupil distance (normalised) — face too close. */
const MAX_IPD = 0.25;

// ---------------------------------------------------------------------------
// Device-specific GPU delegate selection
// ---------------------------------------------------------------------------

/**
 * Determine the best MediaPipe delegate for the current device.
 *
 * Known issues:
 *  - Samsung Galaxy S25 Ultra (Android 15, Chrome 133+): FaceLandmarker stops
 *    detecting faces after the first few frames with GPU delegate.
 *    See: https://github.com/google-ai-edge/mediapipe/issues/5908
 *  - iOS Safari: WebGL2 context creation can fail, causing GPU graphs to error
 *    with "emscripten_webgl_create_context() returned error 0".
 */
export function selectDelegate(): 'GPU' | 'CPU' {
  const ua = navigator.userAgent;

  // Samsung Galaxy S25 — GPU delegate causes detection to freeze after a few frames
  if (/SM-S93[56]/i.test(ua) || /Galaxy S25/i.test(ua)) {
    return 'CPU';
  }

  // iOS Safari — test WebGL2 support; fall back to CPU if unavailable
  if (/iPhone|iPad/.test(ua) && /Safari/.test(ua) && !/CriOS|FxiOS/.test(ua)) {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl2');
      if (!gl) return 'CPU';
    } catch {
      return 'CPU';
    }
  }

  return 'GPU';
}

// ---------------------------------------------------------------------------
// Exported types (for barrel re-export)
// ---------------------------------------------------------------------------

/** Raw MediaPipe landmark coordinate from tasks-vision. */
export interface RawMediaPipeLandmark {
  x: number;
  y: number;
  z: number;
}

/** Result from a single frame of MediaPipe inference. */
export interface MediaPipeResult {
  faceLandmarks: RawMediaPipeLandmark[][];
  facialTransformationMatrixes: Array<{ data: number[] }>;
  faceBlendshapes: unknown[];
  faceDetections: unknown[];
}

/** Options for constructing a MediaPipeTracker. */
export interface MediaPipeTrackerOptions {
  /** Override the GPU/CPU delegate selection. */
  delegate?: 'GPU' | 'CPU';
  /** Enable debug mode (include all 478 landmarks in output). */
  debug?: boolean;
}

// ---------------------------------------------------------------------------
// Internal helpers (exported for unit-testing without instantiating the class)
// ---------------------------------------------------------------------------

/**
 * Convert a MediaPipe normalised landmark to Three.js world-space coordinates.
 *
 * MediaPipe returns x, y in [0, 1] (origin top-left).
 * Three.js uses a right-handed, Y-up coordinate system with origin at centre.
 *
 * Mapping:
 *   x: [0,1] → [-aspect/2, +aspect/2], centred (NO mirror — CSS scaleX(-1)
 *      on the canvas handles the selfie mirror visually)
 *   y: [0,1] → [+0.5, -0.5]  (inverted because Three.js Y increases upward)
 *   z: negated so "closer to camera" becomes positive in Three.js view space
 */
export function landmarkToWorld(
  landmark: { x: number; y: number; z: number },
  videoWidth: number,
  videoHeight: number,
): [number, number, number] {
  const aspect = videoWidth / videoHeight;
  const x = (landmark.x - 0.5) * aspect;  // centre only — CSS handles mirror
  const y = -(landmark.y - 0.5);           // invert + centre
  const z = -landmark.z;                   // negate for Three.js convention
  return [x, y, z];
}

/**
 * Convert a MediaPipe normalised landmark to world-space with X mirrored.
 *
 * Same as landmarkToWorld but negates X for use when the canvas CSS mirror
 * is NOT applied (e.g. snapshot compositing).
 */
export function landmarkToWorldMirrored(
  landmark: { x: number; y: number; z: number },
  videoWidth: number,
  videoHeight: number,
): [number, number, number] {
  const aspect = videoWidth / videoHeight;
  const x = -(landmark.x - 0.5) * aspect; // mirrored X
  const y = -(landmark.y - 0.5);
  const z = -landmark.z;
  return [x, y, z];
}

/**
 * Decompose a column-major 4×4 rotation matrix (16 elements) into Euler angles
 * (pitch / yaw / roll) in **radians**.
 *
 * The matrix layout follows the OpenGL / MediaPipe convention:
 *
 *   [ m0  m4  m8  m12 ]
 *   [ m1  m5  m9  m13 ]
 *   [ m2  m6  m10 m14 ]
 *   [ m3  m7  m11 m15 ]
 *
 * Decomposition uses the ZYX (yaw → pitch → roll) convention consistent with
 * head-pose estimation literature:
 *   pitch = arcsin(-m[9])            (X rotation)
 *   yaw   = atan2(m[8],  m[10])      (Y rotation)
 *   roll  = atan2(m[1],  m[5])       (Z rotation)
 *
 * Edge case: if cos(pitch) ≈ 0 (gimbal lock at ±90°) roll is set to 0 and
 * yaw is read from atan2(-m[2], m[0]).
 */
export function matrixToEuler(
  matrix: number[],
): { pitch: number; yaw: number; roll: number } {
  // Column-major indices:  row + col*4
  // m[row][col] = matrix[row + col * 4]
  const m9  = matrix[9];   // row=1, col=2  → used for pitch
  const m8  = matrix[8];   // row=0, col=2
  const m10 = matrix[10];  // row=2, col=2
  const m1  = matrix[1];   // row=1, col=0
  const m5  = matrix[5];   // row=1, col=1
  const m2  = matrix[2];   // row=2, col=0
  const m0  = matrix[0];   // row=0, col=0

  // Clamp to [-1, 1] to guard against floating-point drift
  const sinPitch = Math.max(-1, Math.min(1, -(m9 ?? 0)));
  const pitch = Math.asin(sinPitch);

  const cosPitch = Math.cos(pitch);

  let yaw: number;
  let roll: number;

  if (Math.abs(cosPitch) < 1e-6) {
    // Gimbal lock — pitch ≈ ±90°
    yaw  = Math.atan2(-(m2 ?? 0), m0 ?? 0);
    roll = 0;
  } else {
    yaw  = Math.atan2(m8 ?? 0, m10 ?? 0);
    roll = Math.atan2(m1 ?? 0, m5 ?? 0);
  }

  return { pitch, yaw, roll };
}

/**
 * Calculate the inter-pupillary distance (IPD) between two landmark points.
 *
 * Works with both normalised MediaPipe coordinates and world-space Point3D.
 */
export function calculateIPD(
  leftPupil: { x: number; y: number; z?: number } | [number, number, number],
  rightPupil: { x: number; y: number; z?: number } | [number, number, number],
): number {
  if (Array.isArray(leftPupil) && Array.isArray(rightPupil)) {
    return Math.hypot(
      rightPupil[0] - leftPupil[0],
      rightPupil[1] - leftPupil[1],
      rightPupil[2] - leftPupil[2],
    );
  }
  const l = leftPupil as { x: number; y: number; z?: number };
  const r = rightPupil as { x: number; y: number; z?: number };
  return Math.hypot(r.x - l.x, r.y - l.y, (r.z ?? 0) - (l.z ?? 0));
}

/**
 * Check whether the detected face is usable for overlay rendering.
 *
 * Returns false when:
 *  - Any landmark is at the image boundary (face is cropped/cut off)
 *  - Inter-pupil distance is out of range (face too far or too close)
 */
export function isTrackingUsable(
  lms: Array<{ x: number; y: number; z: number }>,
): boolean {
  // Face cut-off: check a handful of boundary landmarks
  const BOUNDARY_INDICES = [10, 152, 234, 454]; // forehead, chin, left/right ear
  for (const idx of BOUNDARY_INDICES) {
    const p = lms[idx];
    if (!p) continue;
    if (p.x <= 0.01 || p.x >= 0.99 || p.y <= 0.01 || p.y >= 0.99) {
      return false;
    }
  }

  // Inter-pupil distance range check (normalised coords)
  const left = lms[468];
  const right = lms[473];
  if (left && right) {
    const dx = left.x - right.x;
    const dy = left.y - right.y;
    const ipd = Math.hypot(dx, dy);
    if (ipd < MIN_IPD || ipd > MAX_IPD) {
      return false;
    }
  }

  return true;
}

// ---------------------------------------------------------------------------
// MediaPipeTracker
// ---------------------------------------------------------------------------

export class MediaPipeTracker implements ITracker<EyewearLandmarks> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private faceLandmarker: any = null;
  private _isReady = false;
  private lastGoodLandmarks: EyewearLandmarks | null = null;
  private staleFrameCount = 0;
  /** When true, track() includes all 478 landmarks in rawLandmarks. */
  private _debug = false;
  /** Face shape classifier — cached, recomputes every 30 frames. */
  private _faceShapeClassifier = new FaceShapeClassifier();
  /** Monotonically increasing timestamp for detectForVideo — prevents crash on
   *  non-monotonic timestamps when processing multiple photos in sequence. */
  private lastTimestamp = 0;

  /** Enable/disable debug mode (includes raw 478 landmarks in output). */
  setDebug(enabled: boolean): void {
    this._debug = enabled;
  }

  get isReady(): boolean {
    return this._isReady;
  }

  /**
   * Load the MediaPipe WASM runtime and FaceLandmarker model.
   *
   * Uses a dynamic import of the CDN bundle so that the WASM bytes are never
   * bundled into the host application's JavaScript chunk. Falls back gracefully
   * if the CDN is unreachable.
   */
  async init(): Promise<void> {
    let FaceLandmarker: any; // eslint-disable-line @typescript-eslint/no-explicit-any
    let FilesetResolver: any; // eslint-disable-line @typescript-eslint/no-explicit-any

    try {
      // Dynamic import keeps @mediapipe/tasks-vision off the npm dependency
      // graph; the bundle is fetched from the CDN on first use.
      const vision = await import(/* @vite-ignore */ MEDIAPIPE_CDN);
      FaceLandmarker  = vision.FaceLandmarker;
      FilesetResolver = vision.FilesetResolver;
    } catch (err) {
      throw new Error(
        `MediaPipeTracker: failed to load MediaPipe bundle from CDN. ` +
        `Ensure the host is online and CSP allows ${MEDIAPIPE_CDN}. ` +
        `Original error: ${String(err)}`,
      );
    }

    let wasmFileset: any; // eslint-disable-line @typescript-eslint/no-explicit-any
    try {
      wasmFileset = await FilesetResolver.forVisionTasks(WASM_BASE_URL);
    } catch (err) {
      throw new Error(
        `MediaPipeTracker: FilesetResolver.forVisionTasks() failed. ` +
        `Check that the WASM assets are accessible at ${WASM_BASE_URL}. ` +
        `Original error: ${String(err)}`,
      );
    }

    try {
      const delegate = selectDelegate();
      this.faceLandmarker = await FaceLandmarker.createFromOptions(wasmFileset, {
        baseOptions: {
          modelAssetPath: MODEL_ASSET_PATH,
          delegate,
        },
        outputFaceBlendshapes: true,
        outputFacialTransformationMatrixes: true,
        runningMode: 'VIDEO',
        numFaces: 1,
      });
    } catch (err) {
      throw new Error(
        `MediaPipeTracker: FaceLandmarker.createFromOptions() failed. ` +
        `The model may be unavailable at ${MODEL_ASSET_PATH}. ` +
        `Original error: ${String(err)}`,
      );
    }

    this._isReady = true;
  }

  /**
   * Run a single-frame inference on the supplied video element and return
   * the extracted facial landmarks, or `null` when tracking is lost.
   *
   * Stale-frame policy: if no face is detected for up to STALE_THRESHOLD
   * consecutive frames, the last known good landmarks are returned so that
   * momentary occlusion does not cause the overlay to flicker off. After
   * STALE_THRESHOLD frames of continuous non-detection the method returns
   * `null` so the caller can hide the overlay entirely.
   */
  track(video: HTMLVideoElement): EyewearLandmarks | null {
    if (!this._isReady || !this.faceLandmarker) return null;

    // Guard: skip if the video element has no valid frame data yet.
    // Guard: skip if the element has no valid dimensions.
    // HTMLVideoElement uses videoWidth/videoHeight; HTMLImageElement uses
    // naturalWidth/naturalHeight (for photo mode). Check both.
    const vw = (video as HTMLVideoElement).videoWidth
      || (video as unknown as HTMLImageElement).naturalWidth
      || (video as HTMLElement).clientWidth
      || 0;
    const vh = (video as HTMLVideoElement).videoHeight
      || (video as unknown as HTMLImageElement).naturalHeight
      || (video as HTMLElement).clientHeight
      || 0;
    if (vw === 0 || vh === 0) return null;
    // For video elements, also check readyState (images don't have it)
    if ('readyState' in video && (video as HTMLVideoElement).readyState < 2) return null;

    let result: any; // eslint-disable-line @typescript-eslint/no-explicit-any
    try {
      // Ensure monotonically increasing timestamps — MediaPipe throws if a
      // timestamp <= the previous one is passed (happens with rapid photo uploads).
      let timestamp = performance.now();
      if (timestamp <= this.lastTimestamp) {
        timestamp = this.lastTimestamp + 1;
      }
      this.lastTimestamp = timestamp;
      result = this.faceLandmarker.detectForVideo(video, timestamp);
    } catch {
      // MediaPipe can throw on transient video states (e.g. seeking, tab switch).
      // Treat as no-detection rather than crashing the loop.
      return this.lastGoodLandmarks;
    }

    const faces: unknown[] | undefined = result?.faceLandmarks;
    if (!faces || faces.length === 0) {
      this.staleFrameCount++;
      if (this.staleFrameCount > STALE_THRESHOLD) {
        // Tracking definitively lost — caller should hide the overlay
        return null;
      }
      // Short gap — freeze last known pose to avoid flicker
      return this.lastGoodLandmarks;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const lms: any[] = faces[0] as any[];

    // Reject low-quality detections (face cut off or too far/close)
    if (!isTrackingUsable(lms)) {
      this.staleFrameCount++;
      if (this.staleFrameCount > STALE_THRESHOLD) {
        return null;
      }
      return this.lastGoodLandmarks;
    }

    // A usable face was detected — reset the stale counter
    this.staleFrameCount = 0;

    const w = video.videoWidth  || video.width  || 640;
    const h = video.videoHeight || video.height || 480;

    // Map well-known indices to world-space Point3D values
    const leftPupil    = landmarkToWorld(lms[468], w, h);
    const rightPupil   = landmarkToWorld(lms[473], w, h);
    // Use index 168 (NOSE_BRIDGE_MID — glasses resting point) instead of
    // index 6 (upper bridge, between eyebrows). Index 168 is where real
    // glasses sit on the nose pad area; 6 is ~2% of face height too high.
    // Jewelry/makeup adapters continue to use NOSE_BRIDGE (6) via
    // LANDMARK_INDICES.NOSE_BRIDGE — this change is eyewear-only.
    const noseBridge   = landmarkToWorld(lms[168], w, h);
    const leftTemple   = landmarkToWorld(lms[127], w, h);
    const rightTemple  = landmarkToWorld(lms[356], w, h);

    // Head pose from facial transformation matrix (4×4 column-major, 16 floats)
    const matrices: unknown[] | undefined =
      result?.facialTransformationMatrixes;
    let rotation = { pitch: 0, yaw: 0, roll: 0 };
    let faceMatrix: number[] | undefined;
    if (matrices && matrices.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const mat: any = matrices[0];
      // The matrix object exposes a `data` Float32Array in the Tasks API
      const data: number[] = Array.from(mat?.data ?? mat ?? []);
      if (data.length >= 16) {
        rotation = matrixToEuler(data);
        faceMatrix = data;
      }
    }

    // Confidence: use the first detection score when available, else 1.0
    const detections: unknown[] | undefined = result?.faceBlendshapes;
    let confidence = 1.0;
    if (detections && detections.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const categories: any[] | undefined = (detections[0] as any)?.categories;
      if (categories && categories.length > 0) {
        // blendshapes don't carry a raw confidence score; fall back to 1.0
        // unless the MediaPipe result exposes a score directly
      }
    }
    // Some MediaPipe builds expose a top-level `faceDetections` array
    const faceDetections: unknown[] | undefined = result?.faceDetections;
    if (faceDetections && faceDetections.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const score = (faceDetections[0] as any)?.categories?.[0]?.score;
      if (typeof score === 'number') {
        confidence = score;
      }
    }

    // Map first 468 landmarks to world space (always — needed by FaceOccluder)
    const allLandmarks: [number, number, number][] = new Array(Math.min(lms.length, 468));
    for (let i = 0; i < allLandmarks.length; i++) {
      allLandmarks[i] = landmarkToWorld(lms[i], w, h);
    }

    // Face shape classification (cached, recomputes every 30 frames)
    const shapeResult = this._faceShapeClassifier.classify(allLandmarks);

    const ipd = calculateIPD(leftPupil, rightPupil);

    const landmarks: EyewearLandmarks = {
      leftPupil,
      rightPupil,
      noseBridge,
      leftTemple,
      rightTemple,
      rotation,
      confidence,
      ipd,
      allLandmarks,
      rawNormalizedLandmarks: lms.map((lm: { x: number; y: number; z?: number }) => ({
        x: lm.x,
        y: lm.y,
        ...(lm.z !== undefined ? { z: lm.z } : {}),
      })),
      ...(faceMatrix ? { faceMatrix } : {}),
      ...(shapeResult ? { faceShape: shapeResult } : {}),
    };

    // Include all 478 landmarks in debug mode for wireframe overlay
    if (this._debug) {
      landmarks.rawLandmarks = lms.map(
        (lm: { x: number; y: number; z: number }) => landmarkToWorld(lm, w, h),
      );
    }

    this.lastGoodLandmarks = landmarks;
    return landmarks;
  }

  /**
   * Release the FaceLandmarker and all associated WASM/GPU resources.
   * After calling destroy() the instance must not be reused without calling
   * init() again.
   */
  destroy(): void {
    try {
      this.faceLandmarker?.close();
    } catch {
      // Swallow — some MediaPipe builds throw if close() is called twice
    }
    this.faceLandmarker      = null;
    this._isReady            = false;
    this.lastGoodLandmarks   = null;
    this.staleFrameCount     = 0;
    this._faceShapeClassifier.reset();
  }
}
